'use client';

import type { ReactNode } from 'react';
import { createContext, useState, useTransition, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import type { NavigateOptions } from 'next/dist/shared/lib/app-router-context.shared-runtime';
import { useAppName } from '../AppNameProvider/useAppName';
import { useI18n } from '../../hooks/useI18n/useI18n';
import { useConfig } from '../../hooks/useConfig/useConfig';
import { localizeUrl } from '../../utils/localizeUrl/localizeUrl';
import { isCrossAppLink } from '../../utils/crossAppNavigation/crossAppNavigation';

type NavigateFunction = (href: string, options?: NavigateOptions) => void;

export const PageTransitionContext = createContext<boolean>(false);
export const PageTransitionNavigateContext = createContext<NavigateFunction>(() => {});
export const PageTransitionLinkPendingContext = createContext<(pending: boolean) => void>(() => {});

export const PageTransitionProvider = ({ children }: { children: ReactNode }) => {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const [linkPending, setLinkPending] = useState(false);
  const appName = useAppName();
  const { language, locale } = useI18n();
  const config = useConfig();

  // Combined loading state: true if either programmatic navigation or link navigation is pending
  const loading = isPending || linkPending;

  const navigate = useCallback<NavigateFunction>(
    (href, options) => {
      const localizedHref = href.startsWith('/') ? localizeUrl(href, language, config) : href;
      if (isCrossAppLink(href, locale, appName)) {
        window.location.href = localizedHref;
      } else {
        startTransition(() => {
          router.push(href, options);
        });
      }
    },
    [router, appName, language, config, locale],
  );

  return (
    <PageTransitionContext.Provider value={loading}>
      <PageTransitionNavigateContext.Provider value={navigate}>
        <PageTransitionLinkPendingContext.Provider value={setLinkPending}>
          {children}
        </PageTransitionLinkPendingContext.Provider>
      </PageTransitionNavigateContext.Provider>
    </PageTransitionContext.Provider>
  );
};
